import React, {createContext, FC, useContext, useEffect, useState} from "react";
import {toDomainUser} from "./user";
import {Auth, User, UserAccount} from "./types";
import {UserAccountStore} from "./store";
import {getRedirectResult, getCredentialFromError} from "../../firebase";
import {FirebaseError} from "@firebase/util";
import {beginMergeAccount} from "../link-account/useMergeAccount";
import {AuthProviderType as AuthProviderType} from "../../domain/providers";

export type InitAccountFunc = (user: User) => Promise<void> | undefined

type Props = {
  children: React.ReactNode
  auth: Auth
  initAccount: InitAccountFunc
}

type authCtx = {
  user: User | null
  loading: boolean
  error?: AccountsError
  signOut: () => Promise<void>
};

export type AccountsError = {
  code?: string
  message?: string
};

const AuthContext = createContext<authCtx>({
  user: null,
  loading: true,
  signOut: async () => {return}
});

export const useAuth = () => {
  return useContext(AuthContext);
}

export const AuthProvider: FC<Props> = ({children, auth, initAccount}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<AccountsError>();

  useEffect(() => {
    const unsubscribe = auth.onIdTokenChanged(firebaseUser => {
      setError(undefined);
      if (firebaseUser) {

        const setupUser = async () => {
          const domainUser = await toDomainUser(firebaseUser)
          try {
            await initAccount(domainUser);
            appendToUserAccounts(domainUser);
            setUser(domainUser);
            setLoading(false);
          } catch (err) {
            console.error("Error initialising account", err);
            setLoading(false);
            setError(err as Error);
            setUser(domainUser);
          }
        };
        setupUser()

      } else {
        handleRedirectResult()
        .catch(err => setError(err))
        .finally(() => {
          setLoading(false);
          setUser(null);
          setLoading(false);
          const userAccounts = UserAccountStore.get();
          if (userAccounts.list().length === 1) {
            UserAccountStore.clear();
          }
        })
      }
    })
    return () => unsubscribe();
  }, [])
  const ctx = {user, loading, error, signOut: () => auth.signOut()};
  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>
};

const appendToUserAccounts = (domainUser: User) => {
  const userAccount: UserAccount = {
    displayName: domainUser.displayName || "",
    email: domainUser.email || "",
    provider: domainUser.provider,
    photoUrl: domainUser.photoUrl
  };
  const userAccounts = UserAccountStore.get();
  userAccounts.add(userAccount);
  UserAccountStore.set(userAccounts);
};

export const handleRedirectResult = async (): Promise<FirebaseError | void> => {
  return getRedirectResult()
  .then(() => {return})
  .catch((err: FirebaseError) => {
    if (err.code === "auth/account-exists-with-different-credential") {
      console.error("user exists with different credential", err);
      const email = (err.customData?.email || "") as string;
      const credential = getCredentialFromError(err);
      if (!credential) {
        throw new Error("Credential is required to link account");
      }
      const identityProvider = credential?.providerId as AuthProviderType;
      beginMergeAccount(email, identityProvider, credential);
      return Promise.reject(err);
    }

    if (err.code === "auth/user-disabled") {
      console.info("user is disabled")
      return Promise.reject(err)
    }

    console.error("error on getRedirectResult", err);
    return Promise.reject(err);
  });
}
